import * as Sentry from "@sentry/react";
import React, { useCallback, useEffect, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { Workbox } from "workbox-window";

const DEFAULT_UPDATE_INTERVAL_MS = 1000 * 60; // 1 minute
const DEFAULT_SERVICE_WORKER_PATH = "/serviceWorker.js";

export const ServiceWorkerContext = React.createContext<{
  /** Null indicates that no update is available and vice-versa. */
  updateAndRefresh: (() => void) | null;
}>(null);

/**
 * Registers and monitors for updates of the service worker.
 *
 * We must monitor for updates ourselves because we are a PWA, and thus route changes which would
 * typically trigger a refresh do not.
 * https://developer.chrome.com/docs/workbox/service-worker-lifecycle#manually_triggering_update_checks
 *
 * Much of this is an adpatation of the workbox CDN example:
 * https://developer.chrome.com/docs/workbox/handling-service-worker-updates
 *
 * It is important that this component does not automatically refresh the page when an update is available,
 * else users could be in the middle of something and have their work lost.
 *
 * It is also important that we do not install a service worker the moment one is available, as this leaves
 * the app in an unknown state, where the code for deploy 1 is being backed by the service worker from deploy 2.
 * To solve for this and maintain atomic updates we must activate and reload in unison.
 * More info: https://stackoverflow.com/a/40130378
 */
export const ServiceWorkerProvider: FCwC<{
  updateIntervalMs?: number;
  serviceWorkerPath?: string;
}> = ({
  updateIntervalMs = DEFAULT_UPDATE_INTERVAL_MS,
  serviceWorkerPath = DEFAULT_SERVICE_WORKER_PATH,
  children,
}) => {
  const location = useLocation();
  const [newUpdateWaiting, setNewUpdateWaiting] = React.useState(false);
  const workbox = useMemo(() => {
    const wb = new Workbox(serviceWorkerPath);
    const onWaitingHandler = () => {
      // Don't have to worry about setting it to false because memory will be cleared by the refresh when it gets installed.
      setNewUpdateWaiting(true);

      // If the user refreshes the page, we want to take the chance to update the service worker. They may lose some cached
      // data, but the fact they're they're refreshing means they're probably expecting some changes.
      window.addEventListener("beforeunload", () => {
        wb.messageSkipWaiting();
      });
    };

    // A new service worker is installed and ready to take over.
    wb.addEventListener("waiting", onWaitingHandler);

    // One-time initial configuration of Workbox to register the existing service worker.
    wb.register().catch((e) => {
      if (
        // This is a very common error and typically just means you weren't connected to the
        // internet when the fetch for the update occurs.
        (e.message as string).includes(
          "unknown error occurred when fetching the script",
        )
      ) {
        return;
      }
      Sentry.captureException(e);
    });
    return wb;
  }, [serviceWorkerPath]);

  const updateAndRefresh = useCallback(() => {
    // Configure the waiting service worker to reload the page as soon as it takes control, thereby making the update atomic.
    workbox.addEventListener("controlling", () => window.location.reload());
    workbox.messageSkipWaiting();
  }, [workbox]);

  // Force a SW take-over and refresh if the user is on the homepage.
  useEffect(() => {
    if (newUpdateWaiting && location.pathname === "/") {
      updateAndRefresh();
    }
  }, [newUpdateWaiting, location.pathname, updateAndRefresh]);

  // Check for updates on an interval.
  useEffect(() => {
    const newIntervalId = setInterval(() => {
      workbox.update();
    }, updateIntervalMs);
    return () => {
      clearInterval(newIntervalId);
    };
  }, [workbox, updateIntervalMs]);

  return (
    <ServiceWorkerContext.Provider
      value={{ updateAndRefresh: newUpdateWaiting ? updateAndRefresh : null }}
    >
      {children}
    </ServiceWorkerContext.Provider>
  );
};

export const useServiceWorker = () => {
  const context = React.useContext(ServiceWorkerContext);
  if (context === null) {
    throw new Error(
      "useServiceWorker must be used within a ServiceWorkerProvider",
    );
  }
  return context;
};
